import {
  Button,
  DialogActions,
  DialogTitle,
  Typography,
  Link,
  DialogContent,
} from '@mui/material';

import { enqueueSnackbar } from 'notistack';
import { API } from 'api';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalGeoEvent, RequestedAck, User } from '../../../shared/types';
import { BaseContent } from './BaseContent';
import { PulsepointContent } from './PulsepointContent';
import { ExternalGeoNameMap } from '../../../constants';
import { useAuthState } from '../../../state/useAuthState';
import { usePoisState } from '../../../state/usePoisState';

type ExternalGeoEventDialogContentProps = ExternalGeoEvent;

const useStyles = makeStyles()((theme) => ({
  title: {
    padding: theme.spacing(2, 6, 0, 2),
    lineHeight: '1.5 !important',
  },
  actions: {
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 2, 0, 2),
  },
  link: {
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.accent.main,
    fontWeight: theme.typography.fontWeightBold,
    paddingLeft: 0,
    pointerEvents: 'auto',
  },
  ack: {
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.accent.main,
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: 100,
    pointerEvents: 'auto',
  },
}));

const getComponentBasedOnExternalSource = (
  props: ExternalGeoEventDialogContentProps,
): JSX.Element => {
  switch (props.externalSource) {
    case 'pulsepoint':
      return <PulsepointContent externalGeoEvent={props} />;
    default:
      return <BaseContent externalGeoEvent={props} />;
  }
};

export const postAck = async (
  newestRequestedAck: RequestedAck,
): Promise<void> => {
  await API.post(`signal_acks/acks/`, {
    requestedAckId: newestRequestedAck.id,
  });
};

const getNewestRequestedAck = (
  exGeo: ExternalGeoEvent,
): RequestedAck | null => {
  if (!exGeo.requestedAcks?.length) {
    return null;
  }
  // there can potentially be more than one requested Ack per external geo event.
  // In this case, pull the newest one.
  const newestRequestedAck = exGeo.requestedAcks.sort((a, b) => b.id - a.id)[0];
  return newestRequestedAck || null;
};

const userCanAck = (newestRequestedAck: RequestedAck, user: User): boolean =>
  !newestRequestedAck.acks?.some((ack) => ack.userCreated.id === user.id);

export const getAckedBy = (exGeo: ExternalGeoEvent): string | null => {
  // return the comma separated DisplayName of each ack'ing user.
  if (!exGeo.requestedAcks) {
    return null;
  }

  const allAcks = exGeo.requestedAcks.flatMap((ack) => ack.acks || []);
  const ackedByUsers = allAcks.map((ack) => ack.userCreated.displayName);
  if (ackedByUsers.length === 0) {
    return null;
  }
  return ackedByUsers.join(', ');
};

export const ExternalGeoEventDialogContent = (
  props: ExternalGeoEventDialogContentProps,
): JSX.Element => {
  const { classes } = useStyles();
  const { permalinkUrl, channel, externalSource, data } = props;
  const { t } = useTranslation();
  const { user } = useAuthState();
  const queryClient = useQueryClient();
  const { clearSelectedPois } = usePoisState();

  // permalink isn't guaranteed, backoff to the channel in that case
  const backToSlackLink =
    permalinkUrl ||
    `https://watchduty.slack.com/app_redirect?channel=${channel}`;

  const contentComponent = getComponentBasedOnExternalSource(props);
  const sourceName = ExternalGeoNameMap[externalSource];

  const newestRequestedAck = getNewestRequestedAck(props);
  const ackedBy = getAckedBy(props);
  const handleAckClick = async (): Promise<void> => {
    if (!newestRequestedAck) {
      return;
    }
    try {
      await postAck(newestRequestedAck);
      enqueueSnackbar('You have acknowledged this signal', {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(t('common.errorMessage'), { variant: 'error' });
      return;
    }
    // Invalidate the externalGeoEvent query so we can refresh upstream ack's rather than trying to
    // mutate inline here and close this modal.
    queryClient.invalidateQueries({ queryKey: ['externalGeoEvents'] });
    clearSelectedPois();
  };

  return (
    <>
      <DialogTitle id="alert-dialog-title" className={classes.title}>
        <Typography component="div" variant="body2">
          <b>{sourceName}</b>
        </Typography>
        <Typography component="div" variant="subtitle1">
          {data.address}
        </Typography>
        {ackedBy && (
          <Typography component="div" variant="subtitle1">
            {t('externalGeoEvents.ackedBy')} {ackedBy}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ paddingLeft: 3, paddingBottom: 1 }}>
        {contentComponent}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          href={backToSlackLink}
          target="_blank"
          component={Link}
          className={classes.link}
          variant="text"
        >
          {t('externalGeoEvents.viewInSlack')}
        </Button>
        {newestRequestedAck && userCanAck(newestRequestedAck, user as User) && (
          <Button
            className={classes.ack}
            onClick={handleAckClick}
            variant="text"
          >
            {t('externalGeoEvents.ack')}
          </Button>
        )}
      </DialogActions>
    </>
  );
};
